import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import type { OnDestroy } from '@angular/core';
import type { AnimationItem } from 'lottie-web';
import { LottieComponent } from 'ngx-lottie';
import type { AnimationOptions } from 'ngx-lottie';
import { resolveChallengeLottiePath } from '../challenge-lottie-path.util';

@Component({
  selector: 'app-challenge-fly-animation',
  imports: [LottieComponent, CommonModule],
  templateUrl: './challenge-fly-animation.component.html',
  styleUrls: ['./challenge-fly-animation.component.scss'],
  standalone: true,
})
export class ChallengeFlyAnimationComponent implements OnDestroy {
  @Input() set lottieUrl(value: string) {
    if (!value) {
      return;
    }
    this.configure({ path: value });
  }
  get lottieUrl() {
    return this._lottieUrl;
  }
  private _lottieUrl = '';

  @Output() done = new EventEmitter<void>();

  /** Large fly box so banner icons read clearly in the trail. */
  static readonly BOX_PX = 480;
  lottieBoxStyles: Record<string, string> = {
    width: `${ChallengeFlyAnimationComponent.BOX_PX}px`,
    height: `${ChallengeFlyAnimationComponent.BOX_PX}px`,
  };

  lottieOptions: AnimationOptions = {
    autoplay: false,
    loop: false,
    renderer: 'svg',
  };
  private animationItem?: AnimationItem;
  private completeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** Configure path / size / renderer before the view is attached. */
  configure(opts: {
    path: string;
    widthPx?: number;
    heightPx?: number;
    autoplay?: boolean;
    renderer?: 'svg' | 'canvas';
  }): void {
    const path = resolveChallengeLottiePath(opts.path);
    this._lottieUrl = path;
    if (opts.widthPx != null && opts.heightPx != null) {
      this.lottieBoxStyles = {
        width: `${opts.widthPx}px`,
        height: `${opts.heightPx}px`,
      };
    }
    this.lottieOptions = {
      ...this.lottieOptions,
      path,
      loop: false,
      autoplay: opts.autoplay ?? false,
      renderer: opts.renderer ?? 'svg',
    } as unknown as AnimationOptions;
  }

  private readonly onComplete = (): void => {
    this.completeTimeoutId = setTimeout(() => this.done.emit(), 100);
  };

  onLottieCreated(anim: AnimationItem) {
    this.animationItem = anim;
    // glow.json (and some others) have a late in-point — start from firstFrame.
    const start = typeof anim.firstFrame === 'number' ? anim.firstFrame : 0;
    try {
      anim.goToAndPlay(start, true);
    } catch {
      anim.play();
    }
    anim.addEventListener('complete', this.onComplete);
  }

  ngOnDestroy(): void {
    if (this.completeTimeoutId != null) {
      clearTimeout(this.completeTimeoutId);
      this.completeTimeoutId = null;
    }
    this.animationItem?.removeEventListener('complete', this.onComplete);
  }
}
